import * as React from 'react';
import * as navigate from 'app/area/shared/navigation';
import { Paper, PromptConfirmation } from 'ui';
import { withPrompt, PromptProps, SpinLoader } from 'app/components';
import { STATE } from 'app/constants';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { containerFactory, DefaultContainerProps } from 'app/infrastructure/redux';
import { hasTranslationsLoaded, translationChanged } from 'app/infrastructure/translate/selectors';
import { OptionsProps, withOptions } from 'app/components/options-handler';
import { importSetAdminTranslation } from '../constants';
import { ImportSetAdminState } from '../types-store';
import { ImportSetFormSections } from './components/import-set-form-sections';
import { actionCreators } from './actions';
import { ImportSetFormSelectorProps, selectors } from './selectors';
import { ImportSetItem } from './types-store';
import { getImportSetAdminAccess } from 'app/area/import-sets-administration/get-access';
import { scrollToTop } from 'app/infrastructure/platform/routing';
import { ImportSetActionType } from 'app/area/import-sets-administration/form/types';
import { routeConstants } from 'app/routes/route-constants';

type ImportSetFormProps = DefaultContainerProps
  & OptionsProps
  & PromptProps
  & ImportSetAdminState
  & ImportSetFormSelectorProps
  & typeof actionCreators;

interface ImportSetFormState {
  showTranslations: boolean;
}

class ImportSetFormComponent extends React.PureComponent<ImportSetFormProps, ImportSetFormState> {
  public static statePath: string = STATE.importSetAdministration;

  public state: Readonly<ImportSetFormState> = {
    showTranslations: false
  };

  public componentDidMount() {
    scrollToTop();
    this.props.loadLanguageOptions();

    if (hasTranslationsLoaded(this.props.translation)) {
      this.initForm();
    }
  }

  public componentDidUpdate(prevProps: ImportSetFormProps) {
    if (this.props.id !== prevProps.id ||
      (this.props.params?.actionType !== prevProps.params?.actionType
        && this.props.params?.actionType === ImportSetActionType.duplicate) ||
      translationChanged(this.props.translation, prevProps.translation)) {
      this.initForm();
    }

    if (prevProps?.form?.validation?.language !== this.props?.form?.validation?.language) {
      this.updateTranslationsToggle();
    }
  }

  public componentWillUnmount() {
    this.props.clearForm();
  }

  private initForm = () => {
    const { id, sharedState, params: { actionType },
      createImportSet, loadImportSet, displayError, locale, translation } = this.props;

    const importSetAccess = getImportSetAdminAccess(sharedState);
    if (!importSetAccess) {
      displayError(translation.no_access_to_import_set_admin);
      navigate.toStartupView(true);
      return;
    }

    if (isDefinedIncludingZero(id)) {
      loadImportSet(id, actionType, locale);
    } else {
      createImportSet();
    }
  };

  private switchTranslationsToggle = (showTranslations: boolean) => this.setState({ showTranslations });

  private updateTranslationsToggle = () => {
    if (!!this.props?.form?.validation?.language) {
      this.switchTranslationsToggle(true);
    }
  };

  private saveImportSet = (importSet: ImportSetItem) => {
    const { params: { actionType } = {}, saveImportSet, translation } = this.props;

    const actionTypeId = ImportSetActionType[actionType];
    const successMessage = (isDefinedIncludingZero(importSet?.id)
      && actionTypeId === ImportSetActionType.edit)
      ? translation.import_set_updated
      : translation.import_set_created;

    saveImportSet(actionTypeId, importSet, successMessage, translation.import_set_validation_error);
  };

  private duplicateImportSet = () => {
    navigate.to({
      pathname: routeConstants.importSetDuplicateFor(this.props.id)
    });
  }

  private deleteImportSet = () => {
    const { id, form: { importSetItem: { language: importSetLanguage = [] } = {} } = {},
      sharedState: { language: { value: languageValue } = {} } = {},
      translation, deleteImportSet } = this.props;

    deleteImportSet(
      id,
      importSetLanguage.find(option => option.value === languageValue)?.description,
      translation.import_set_deleted
    );
  };

  public render() {
    const { section, sharedState, form, translation,
      options,
      params: { actionType },
      loadOptions,
      updateItem, changeLanguage, copyLanguageToAll, updateValidation,
      resetValidation, displayError
    } = this.props;
    const mainLanguage = sharedState?.language?.value ?? undefined;
    const locale = sharedState?.locale || '';
    const translationAdminAccess = sharedState?.access?.adminPanel?.translation?.administrationCentral;

    return <div className="entity-view">
      <Paper componentId="import-set-paper" className={undefined}>
        {!!this.props.shouldDisplayModal &&
          <PromptConfirmation
            title={translation.cancel_changes}
            description={translation.are_you_sure}
            continueText={translation.undo_all}
            cancelText={translation.go_back}
            onContinue={this.props.continue}
            onCancel={this.props.cancel} />}

        {!form.languageOptions || !form.importSetItem || form.fetching
          ? <SpinLoader />
          : <ImportSetFormSections
            actionType={ImportSetActionType[actionType]}
            formState={form}
            mainLanguage={mainLanguage}
            saveImportSet={this.saveImportSet}
            duplicateImportSet={this.duplicateImportSet}
            deleteImportSet={this.deleteImportSet}
            updateItem={updateItem}
            changeLanguage={changeLanguage}
            copyLanguageToAll={copyLanguageToAll}
            options={options}
            loadOptions={loadOptions}
            updateValidation={updateValidation}
            resetValidation={resetValidation}
            displayError={displayError}
            section={section}
            locale={locale}
            translation={translation}
            translationAdminAccess={translationAdminAccess}
            showTranslations={this.state.showTranslations}
            switchTranslationsToggle={this.switchTranslationsToggle} />}
      </Paper>
    </div>;
  }
}

const container = containerFactory(
  withPrompt(withOptions(ImportSetFormComponent)),
  actionCreators, selectors, importSetAdminTranslation
);

export { container as ImportSetForm };
